// The approval queue as Home and Pending both render it: the live
// `/v1/approvals` read (`useApprovals` — re-read on every `approvals` topic
// change, never painted from the echo) plus the per-row APPROVE / SKIP decision.
// One hook so the two routes cannot diverge again — they had already
// invalidated the counts two different ways.
//
// WHY A DECIDED SET. The decision's re-read (`onSettled`) can be overtaken: a
// read that left before the decision committed lands after it and still lists
// the row, with live buttons — a second click that can only 409 over a success.
// So a row whose decision the server ACCEPTED is hidden from that moment, keyed
// by the head the decision was OF (plan 101 M2: a new push re-asks on a new head,
// and that ask shows). The hide is a BRIDGE to the server's answer, not a verdict
// of its own: the first read that no longer lists the PR drops the entry, so the
// server is the arbiter again and a PR sent back at the same head shows.
//
// WHO MAY DECIDE. Both decision routes need `write:prs`
// (`apps/api/src/routes/prs.ts`), which a member does not hold; a member's
// Approve answered 403 (#7426). `mayDecide` is `/v1/me`'s answer
// (`lib/scope-gate.ts`), and `decide` refuses unsent unless it is `allowed`.

import { Scope } from '@developerz/domain';
import { useQueryClient } from '@tanstack/solid-query';
import { createEffect, createMemo, createSignal, on } from 'solid-js';
import { t } from '../i18n';
import { APPROVED_PRS_QUERY_KEY } from './approved-prs';
import { createMutation } from './mutation';
import { type ApprovalRow, useApprovals } from './overview';
import { OVERVIEW_COUNTS_QUERY_KEY } from './overview-counts';
import { decidePending, type PendingDecision } from './pr-detail';
import { useScopeVerdict } from './scope-gate';

const decidedKey = (prId: string, headSha: string | null): string => `${prId}@${headSha ?? ''}`;

/**
 * The decided entries a read still vouches for: an entry whose PR the read no
 * longer lists is the server's again (header). Same set back when nothing drops.
 */
function pruneDecided(
  decided: ReadonlySet<string>,
  rows: readonly ApprovalRow[],
): ReadonlySet<string> {
  const listed = new Set(rows.map((r) => r.id));
  const kept = [...decided].filter((key) => listed.has(key.slice(0, key.lastIndexOf('@'))));
  return kept.length === decided.size ? decided : new Set(kept);
}

/** The queue a route renders. */
export function useApprovalQueue(token: string | null) {
  const approvals = useApprovals(token);
  const mayDecide = useScopeVerdict(Scope.WritePrs, token);
  const queryClient = useQueryClient();
  const [decided, setDecided] = createSignal<ReadonlySet<string>>(new Set());
  // Each landed read is the server's word on which decided PRs are still queued.
  createEffect(
    on(
      () => approvals.data,
      (rows) => {
        if (rows !== undefined) setDecided((prev) => pruneDecided(prev, rows));
      },
    ),
  );
  const rows = createMemo((): readonly ApprovalRow[] =>
    (approvals.data ?? []).filter((r) => !decided().has(decidedKey(r.id, r.head_sha))),
  );
  // Server truth either way (a 409 raced decision also resolves here): the queue,
  // the counts behind the sidebar badge / Home's strip, and the approved list an
  // approval moves the PR onto all re-read before the row unlocks.
  const decision = createMutation({
    call: async (prId: string, verdict: PendingDecision, headSha: string): Promise<string> => {
      await decidePending(prId, verdict, headSha, token);
      return decidedKey(prId, headSha);
    },
    fallback: t('pending.decide_error'),
    onSuccess: (key) => setDecided((prev) => new Set(prev).add(key)),
    onSettled: () =>
      Promise.all([
        approvals.refetch(),
        queryClient.invalidateQueries({ queryKey: [OVERVIEW_COUNTS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [APPROVED_PRS_QUERY_KEY] }),
      ]),
  });
  const decide = (prId: string, verdict: PendingDecision, headSha: string): void => {
    if (mayDecide() === 'allowed') void decision.run(prId, verdict, headSha);
  };
  return { approvals, rows, decision, decide, mayDecide };
}
